import { defineQuery, groq } from 'next-sanity';
import { heroBannerSlideshowProjection, PAGE_BUILDER_PROJECTION } from '../queries/pageBuilderProjection';
import { bynderImageProjection } from '../queries/projection';
import { SEO_PROJECTION } from '../queries/seo';

export const GET_LEVEL_1_PRODUCTS = defineQuery(`
  *[_type == "structaProductLevel1"]{
    _id,
    _type,
    name,
    "slug": slug.current}
  `);

export const GET_SUB_LEVEL_PRODUCTS = defineQuery(`
  *[
    (_type == "structaProductLevel2" && references($level1Id)) ||
    (_type == "structaProductLevel3" && references($level1Id))
  ]{
    _id,
    _type,
    name,
    "slug": slug.current,
    "parentPage": parentPage->{
      _id,
      _type,
      name,
      "slug": slug.current
    }
  }
`);
export const structaLevel3Slugs = defineQuery(`
  *[(_type == "structaProductLevel2" && defined(slug.current)) || (_type == "structaProductLevel3" && defined(slug.current))]
  {"slug": slug.current}
`);

export const articleSlugs = defineQuery(`
  *[_type == "article" && type == $type && defined(slug.current)]
  {"slug": slug.current}
`);

export const settingsQuery = defineQuery(`*[_type == "settings"][0]`);

const postFields = /* groq */ `
  _id,
  "status": select(_originalId in path("drafts.**") => "draft", "published"),
  "title": coalesce(title, "Untitled"),
  "slug": slug.current,
  excerpt,
  coverImage,
  "date": coalesce(date, _updatedAt),
  "author": author->{firstName, lastName, picture},
`;

export const LINK_REFERENCE = /* groq */ `
  _type == "link" => {
    "page": page->slug.current,
    "post": post->slug.current
  }
`;

// const linkFields = /* groq */ `
//   link {
//     ...,
//     ${linkReference}
//   }
// `;

export const getPageQuery = defineQuery(/* groq */ `
  *[_type == 'page' && slug.current == $slug][0]{
    _id,
    _type,
    name,
    slug,
    heading,
    subheading,
    ${PAGE_BUILDER_PROJECTION},
    ${SEO_PROJECTION},
  }
`);

export const structaLevel2Slugs = defineQuery(/* groq */ `
  *[_type == "structaProductLevel2" && defined(slug.current)]{
    "slug": slug.current
  }
`);

export const getStructaLevel2ProductQuery = defineQuery(/* groq */ `
  *[
    _type == "structaProductLevel2" &&
    slug.current == $slug &&
    parentPage->slug.current == $parentSlug
  ][0]{
    _id,
    _type,
    name,
    slug,
    description,
    isIncludedContactSupport,
    ${PAGE_BUILDER_PROJECTION},
    ${SEO_PROJECTION},
  }
`);

export const getStructaLevel1ProductQuery = defineQuery(/* groq */ `
  *[_type == "structaProductLevel1" && slug.current == $slug][0]{
    _id,
    _type,
    name,
    heading,
    subheading,
    isIncludedContactSupport,
    ${PAGE_BUILDER_PROJECTION},
    ${SEO_PROJECTION},
  }
`);

export const getStructaLevel3ProductQuery = defineQuery(/* groq */ `
  *[
    _type == 'structaProductLevel3' &&
    slug.current == $slug &&
    parentPage->slug.current == $productSlug &&
    parentPage->parentPage->slug.current == $parentSlug
  ][0]{
    _id,
    _type,
    name,
    slug,
    heading,
    subheading,
    productSummary,
    isIncludedContactSupport,
    "thumbnailImage": {
      "image": customSeo.metaImage,
      "bynderImage": customSeo.metaImage,
      "imageDescription": customSeo.metaImage.textMetaproperties[name == 'Alt_text'][0].value
    },
    ${PAGE_BUILDER_PROJECTION},
    ${SEO_PROJECTION},
  }
`);

export const getArticleQuery = defineQuery(/* groq */ `
  *[_type == 'article' && type == $type && slug.current == $slug][0]{
    ...,
    _id,
    _type,
    title,
    slug,
    author->{
      _id, name,
      ${bynderImageProjection('avatar', 'bynderAvatar')}
    },
    "articleCategory": articleCategory->{_id, name},
    "articleProducts": coalesce(articleProducts[]->{_id, name}, []),
    "articleApplications": coalesce(articleApplications[]->{_id, name}, []),
    "shareIcons": shareIcons[]->{
      shareType,
      "sharePath": select(
        shareType == "facebook" => "https://www.facebook.com/sharer/sharer.php?u=" + ^.type + "/" + ^.slug.current,
        shareType == "linkedin" => "https://www.linkedin.com/shareArticle?mini=true&url=" + ^.type + "/" + ^.slug.current,
        shareType == "pinterest" => "https://pinterest.com/pin/create/button/?url=" + ^.type + "/" + ^.slug.current,
        shareType == "linked" => "/" + ^.type + "/" + ^.slug.current,
        shareType == "share" => "mailto:?subject=" + ^.title + "&body=" + ^.title + "%0A/" + ^.type + "/" + ^.slug.current,
      )
    },
    heroBanner{
      ${heroBannerSlideshowProjection}
    },
    isIncludedContactSupport,
    isIncludedAskGreg,
    ${PAGE_BUILDER_PROJECTION},
    ${SEO_PROJECTION},
  }
`);

export const sitemapData = defineQuery(`
  [
    ...*[_type == "homePage" && _id == "homePage"]{
      _type,
      _updatedAt
    },
    ...*[_type == "genericPage" && defined(slug.current)]{
      _type,
      "slug": slug.current,
      _updatedAt
    },
    ...*[_type == "article" && defined(slug.current)]{
      _type,
      "slug": slug.current,
      "category": type,
      _updatedAt
    },
    ...*[
      _type == "productsList" &&
      defined(subcategory->slug.current) &&
      defined(subcategory->parentCategory->slug.current)
    ]{
      _type,
      "slug": subcategory->slug.current,
      "parentSlug": subcategory->parentCategory->slug.current,
      _updatedAt
    },
    ...*[_type == "structaProductLevel1" && defined(slug.current)]{
      _type,
      "slug": slug.current,
      _updatedAt
    },
    ...*[_type == "product" && defined(slug.current) && defined(subcategory->slug.current) && defined(subcategory->parentCategory->slug.current)]{
      _type,
      "slug": slug.current,
      "subcategorySlug": subcategory->slug.current,
      "parentSlug": subcategory->parentCategory->slug.current,
      _updatedAt
    },
    ...*[_type == "structaProductLevel2" && defined(slug.current) && defined(parentPage->slug.current)]{
      _type,
      "slug": slug.current,
      "parentSlug": parentPage->slug.current,
      _updatedAt
    },
    ...*[_type == "structaProductLevel3" && defined(slug.current) && defined(parentPage->slug.current)]{
      _type,
      "slug": slug.current,
      "parentSlug": parentPage->slug.current,
      "grandparentSlug": parentPage->parentPage->slug.current,
      _updatedAt
    },
    ...*[_type == "documentPage" && defined(slug.current)]{
      _type,
      "slug": slug.current,
      _updatedAt
    },
    ...*[_type == "documentFile" && defined(bynderDocument)]{
      _type,
      "fileUrl": coalesce(
        bynderDocument.originalUrl,
        bynderDocument.selectedUrl,
        bynderDocument.files.original.url
      ),
      bynderDocument,
      _updatedAt
    },
    ...*[_type == "storeLocator"]{
      _type,
      _updatedAt
    },
    ...*[_type == "contactUs"]{
      _type,
      _updatedAt
    },
    ...*[_type == "faqs"]{
      _type,
      _updatedAt
    }
  ]
`);

export const allPostsQuery = defineQuery(`
  *[_type == "post" && defined(slug.current)] | order(date desc, _updatedAt desc) {
    ${postFields}
  }
`);

export const morePostsQuery = defineQuery(`
  *[_type == "post" && _id != $skip && defined(slug.current)] | order(date desc, _updatedAt desc) [0...$limit] {
    ${postFields}
  }
`);

export const postQuery = defineQuery(`
  *[_type == "post" && slug.current == $slug] [0] {
    content[]{
    ...,
    markDefs[]{
      ...,
      ${LINK_REFERENCE}
    }
  },
    ${postFields}
  }
`);

export const postPagesSlugs = defineQuery(`
  *[_type == "post" && defined(slug.current)]
  {"slug": slug.current}
`);

export const pagesSlugs = defineQuery(`
  *[_type == "page" && defined(slug.current)]
  {"slug": slug.current}
`);

export const getStoreLocatorList = defineQuery(`
  *[_type == "storeLocator"][0]{
    _id,
    _type,
    _createdAt,
    _updatedAt,
    _rev,
    name,
    headline,
    isIncludedProductRangeFilter,
    notFoundHeading,
    notFoundText,
    getDirectionLabel,
    listViewLabel,
    mapViewLabel,
    // Fetch the full store data for each reference in the storesList array.
    "storesList": storesList[]->{
      _id,
      _type,
      // Add other fields you want to fetch from the SingleStore document
      name,
      address,
      location,
      // etc.
    },
    isIncludedContactSupport
  }
`);

export const PRODUCT_BY_SLUG_QUERY = `
*[
  _type == "product" &&
  slug.current == $slug &&
  subcategory->slug.current == $productSlug &&
  subcategory->parentCategory->slug.current == $parentSlug
][0] {
  _id,
  name,
  slug,
  description,
  thumbnail,
  "category": category->{
    _id,
    name
  },
  "subcategory": subcategory->{
    _id,
    name
  },
  "image": thumbnail,
  "viewDetailLink": {
    "type": "internal",
    "url": "/products/" + slug.current
  },
  ${SEO_PROJECTION}
}
`;

export const blogDetailSlugs = groq`
  *[_type == "blogDetail" && defined(slug.current)][]{
    "slug": slug.current
  }
`;

export const blogDetailQuery = groq`
  *[_type == "blogDetail" && slug.current == $slug][0]{
    _id,
    title,
  }
`;

export const redirectQuery = groq`*[_type == "redirect"]{
  "source":source.current,
  "destination":destination.current,
  externalUrl,
  permanent
}`;
